/* eslint-disable max-lines-per-function */
import type { PlayerController } from "@lumi/player-core";
import React from "react";

export function usePlayerShortcuts(params: {
  controllerRef: React.RefObject<PlayerController | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  time: number;
  duration: number;
  setIsPlaying: (b: boolean) => void;
  setActiveCaption: React.Dispatch<React.SetStateAction<number | "off">>;
  setShowHUD: (b: boolean) => void;
  onBack?: () => void;
}): void {
  const { controllerRef, containerRef, videoRef, time, duration, setIsPlaying, setActiveCaption, setShowHUD, onBack } =
    params;

  // Helper functions for playback control
  const togglePlay = React.useCallback(() => {
    const ctrl = controllerRef.current;
    const v = videoRef.current;
    if (!ctrl || !v) return;

    if (v.paused) {
      ctrl
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    } else {
      ctrl
        .pause()
        .then(() => setIsPlaying(false))
        .catch(() => undefined);
    }
  }, [controllerRef, videoRef, setIsPlaying]);

  const skipForward = React.useCallback(
    (seconds: number = 10) => {
      const ctrl = controllerRef.current;
      if (!ctrl) return;
      ctrl.seek(Math.min(duration, time + seconds));
    },
    [controllerRef, duration, time],
  );

  const skipBack = React.useCallback(
    (seconds: number = 10) => {
      const ctrl = controllerRef.current;
      if (!ctrl) return;
      ctrl.seek(Math.max(0, time - seconds));
    },
    [controllerRef, time],
  );

  const toggleCaptions = React.useCallback(() => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;

    setActiveCaption((prev) => {
      const next = prev === "off" ? 0 : "off";
      ctrl.setCaption(next as number | "off");
      return next as typeof prev;
    });
  }, [controllerRef, setActiveCaption]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const ctrl = controllerRef.current;
      const v = videoRef.current;
      if (!ctrl || !v) return;
      switch (e.key) {
        case " ": // Space
          e.preventDefault();
          togglePlay();
          break;
        case "ArrowLeft":
          e.preventDefault();
          skipBack(e.shiftKey ? 10 : 5);
          break;
        case "ArrowRight":
          e.preventDefault();
          skipForward(e.shiftKey ? 10 : 5);
          break;
        case "f":
        case "F":
          e.preventDefault();
          if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => undefined);
          } else {
            // Use container for fullscreen to preserve custom subtitle overlay
            const container = containerRef.current;
            if (container) {
              container.requestFullscreen().catch(() => undefined);
            }
          }
          break;
        case "c":
        case "C":
          e.preventDefault();
          toggleCaptions();
          break;
        case "?":
          e.preventDefault();
          setShowHUD(true);
          break;
        case "Escape":
          e.preventDefault();
          onBack?.();
          break;
        default:
          break;
      }
    };
    globalThis.addEventListener("keydown", onKey);
    return () => globalThis.removeEventListener("keydown", onKey);
  }, [controllerRef, containerRef, videoRef, togglePlay, skipForward, skipBack, toggleCaptions, setShowHUD, onBack]);

  // Listen for Electron menu playback commands
  React.useEffect(() => {
    const handleElectronPlayback = (e: Event) => {
      const customEvent = e as CustomEvent<{ action: string }>;
      const { action } = customEvent.detail;

      switch (action) {
        case "toggle":
          togglePlay();
          break;
        case "skip-forward":
          skipForward(10);
          break;
        case "skip-back":
          skipBack(10);
          break;
        case "toggle-captions":
          toggleCaptions();
          break;
        default:
          break;
      }
    };

    globalThis.addEventListener("electron-playback", handleElectronPlayback);
    return () => globalThis.removeEventListener("electron-playback", handleElectronPlayback);
  }, [togglePlay, skipForward, skipBack, toggleCaptions]);
}
